import {
  formatSsn,
  GLOBAL_SEARCH_MAX_LENGTH,
  GLOBAL_SEARCH_MIN_LENGTH,
  type ConsultationSearchHitDto,
  type PatientSearchHitDto,
} from '@osteopro/shared';
import { cn, Input } from '@osteopro/ui';
import { FileText, Loader2, Search, User, type LucideIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useGlobalSearch } from '../../hooks/api/use-search.js';
import { useDebouncedValue } from '../../hooks/use-debounced-value.js';
import { fullName } from '../../lib/format.js';
import { usePatientTabs } from './patient-tabs.js';

interface SearchOption {
  id: string;
  select: () => void;
}

/**
 * Recherche globale de l'en-tête, reprise de la V1 : les dossiers puis les consultations, cinq par
 * groupe, dans une liste déroulante sous le champ. Au clavier, Ctrl/⌘ + K place le curseur dans le
 * champ (instance qui porte `shortcut`), les flèches parcourent les résultats, Entrée ouvre celui
 * qui est désigné — le premier à défaut — et Échap referme la liste.
 */
export function GlobalSearch({
  className,
  onNavigate,
  shortcut = false,
}: {
  className?: string;
  onNavigate?: () => void;
  shortcut?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  // Le résultat désigné au clavier ne vaut que pour le terme qui l'a produit.
  const [cursor, setCursor] = useState({ term: '', index: -1 });
  const [, navigate] = useLocation();
  const { openTab } = usePatientTabs();
  const container = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const debounced = useDebouncedValue(term, 300).trim();
  const { data, isFetching, isError } = useGlobalSearch(debounced);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  useEffect(() => {
    if (!shortcut) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      input.current?.focus();
      input.current?.select();
      setOpen(true);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [shortcut]);

  const finish = (path: string) => {
    navigate(path);
    setTerm('');
    setOpen(false);
    onNavigate?.();
  };
  const openPatient = (patient: PatientSearchHitDto) => {
    openTab({ id: patient.id, firstName: patient.firstName, lastName: patient.lastName });
    finish(`/patients/${patient.id}`);
  };
  const openConsultation = (consultation: ConsultationSearchHitDto) => {
    finish(`/consultations/${consultation.id}`);
  };

  const patients = data?.patients ?? [];
  const consultations = data?.consultations ?? [];
  const optionId = (kind: 'patient' | 'consultation', id: string) => `${listId}-${kind}-${id}`;
  // Une seule suite pour le clavier : les dossiers, puis les consultations, dans l'ordre affiché.
  const options: SearchOption[] = [
    ...patients.map((patient) => ({
      id: optionId('patient', patient.id),
      select: () => {
        openPatient(patient);
      },
    })),
    ...consultations.map((consultation) => ({
      id: optionId('consultation', consultation.id),
      select: () => {
        openConsultation(consultation);
      },
    })),
  ];
  const active = cursor.term === debounced ? cursor.index : -1;
  // Comme la V1, la liste s'ouvre sur une réponse : pendant la première, seul le champ tourne.
  const answered = isError || (data !== undefined && (options.length > 0 || !isFetching));
  const showPanel = open && debounced.length >= GLOBAL_SEARCH_MIN_LENGTH && answered;
  const showList = showPanel && !isError && options.length > 0;

  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' });

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' && !open) {
      setOpen(true);
      return;
    }
    if (!showList) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const start = active === -1 ? (step === 1 ? -1 : 0) : active;
      const next = (start + step + options.length) % options.length;
      setCursor({ term: debounced, index: next });
      const target = options[next];
      if (target) document.getElementById(target.id)?.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      options[Math.max(active, 0)]?.select();
    }
  };

  return (
    <div ref={container} className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={input}
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={showList ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={showList ? options[active]?.id : undefined}
          aria-keyshortcuts={shortcut ? 'Control+K Meta+K' : undefined}
          className="pl-10"
          placeholder={t('shell:search.placeholder')}
          maxLength={GLOBAL_SEARCH_MAX_LENGTH}
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          data-testid="input-global-search"
        />
        {isFetching && (
          <Loader2
            className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
            data-testid="state-search-loading"
          />
        )}
      </div>

      {showPanel && (
        <div
          className="absolute top-full z-50 mt-2 max-h-[400px] w-full min-w-[320px] overflow-y-auto rounded-md border bg-popover shadow-lg"
          data-testid="search-results-dropdown"
        >
          {isError && (
            <p
              className="p-4 text-center text-sm text-destructive"
              data-testid="state-search-error"
            >
              {t('shell:search.error')}
            </p>
          )}
          {!isError && options.length === 0 && (
            <p
              className="p-4 text-center text-sm text-muted-foreground"
              data-testid="state-search-empty"
            >
              {t('shell:search.empty')}
            </p>
          )}
          {showList && (
            <div id={listId} role="listbox" aria-label={t('shell:search.title')}>
              {patients.length > 0 && (
                <ResultGroup
                  label={t('shell:search.group_patients')}
                  testId="text-search-group-patients"
                  className={cn(consultations.length > 0 && 'border-b')}
                >
                  {patients.map((patient, index) => (
                    <ResultRow
                      key={patient.id}
                      optionId={optionId('patient', patient.id)}
                      active={index === active}
                      icon={User}
                      iconClassName="bg-primary/10 text-primary"
                      title={fullName(patient)}
                      subtitle={
                        patient.ssn
                          ? formatSsn(patient.ssn, patient.ssnCountry)
                          : (patient.email ?? patient.phone ?? t('shell:search.no_contact'))
                      }
                      onSelect={() => {
                        openPatient(patient);
                      }}
                      testId={`search-result-patient-${patient.id}`}
                    />
                  ))}
                </ResultGroup>
              )}
              {consultations.length > 0 && (
                <ResultGroup
                  label={t('shell:search.group_consultations')}
                  testId="text-search-group-consultations"
                >
                  {consultations.map((consultation, index) => (
                    <ResultRow
                      key={consultation.id}
                      optionId={optionId('consultation', consultation.id)}
                      active={patients.length + index === active}
                      icon={FileText}
                      iconClassName="bg-info/10 text-info"
                      title={consultation.title ?? t('shell:search.untitled')}
                      subtitle={`${fullName({
                        firstName: consultation.patientFirstName,
                        lastName: consultation.patientLastName,
                      })} • ${date.format(new Date(consultation.startedAt))}`}
                      onSelect={() => {
                        openConsultation(consultation);
                      }}
                      testId={`search-result-consultation-${consultation.id}`}
                    />
                  ))}
                </ResultGroup>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({
  label,
  testId,
  className,
  children,
}: {
  label: string;
  testId: string;
  className?: string;
  children: ReactNode;
}) {
  const labelId = useId();
  return (
    <div role="group" aria-labelledby={labelId} className={className}>
      <div
        id={labelId}
        className="px-3 py-2 text-xs font-semibold uppercase text-muted-foreground"
        data-testid={testId}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function ResultRow({
  optionId,
  active,
  icon: Icon,
  iconClassName,
  title,
  subtitle,
  onSelect,
  testId,
}: {
  optionId: string;
  active: boolean;
  icon: LucideIcon;
  iconClassName: string;
  title: string;
  subtitle: string;
  onSelect: () => void;
  testId: string;
}) {
  return (
    <button
      id={optionId}
      type="button"
      role="option"
      aria-selected={active}
      className={cn(
        'toggle-elevate flex w-full items-start gap-3 border-0 px-3 py-2 text-left hover-elevate active-elevate-2',
        active && 'toggle-elevated',
      )}
      onClick={onSelect}
      data-testid={testId}
    >
      <span
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
          iconClassName,
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{title}</span>
        <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
      </span>
    </button>
  );
}
